/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { fileViewModes } from "./constants";
import { untranslated } from "@Helpers/types/i18n";
import { fileManagerBreadCrumb } from "@Actions/fileUpload";
import type { BreadcrumbItem } from "@ServerTypes/folders/folders.schema";
import type { FileSystemReadResponse, FileSystemDeleteResponse } from "./fileSystemServer.schema";

interface UseFileManagerComponentProps {
  fileManagerData?: any;
  setFileManagerData: Function;
  setCurrentFolder?: Function;
  beforeSending?: Function;
  onSuccess?: Function;
  showFolderTree?: boolean;
  path?: string;
  workspaceId?: string;
  onDeleteSuccess?: Function;
  onDeleteError?: Function;
  disableAutoLoad?: boolean;
  // Project-level ("Folders") mode: the tree shows every Space of the project as a top-level root. The read
  // hits /readFiles with a projectId, returning all Space roots + only the focus Space's first level (lazy);
  // other Spaces and folders load their contents on demand when expanded.
  projectMode?: boolean;
  projectId?: string;
}

function useFileManagerComponent({
  fileManagerData = { cwd: {}, files: [] },
  setFileManagerData,
  setCurrentFolder = () => {},
  beforeSending = () => {},
  onSuccess = () => {},
  showFolderTree = true,
  path,
  workspaceId,
  onDeleteSuccess = () => {},
  onDeleteError = () => {},
  disableAutoLoad = false,
  projectMode = false,
  projectId,
}: UseFileManagerComponentProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // State — in project mode nothing is selected until a Space root is picked (the '0' sentinel means
  // "the single space root", which is ambiguous when there are many Space roots).
  const [selectedFolder, setSelectedFolder] = useState<any>(projectMode ? {} : { id: "0" });
  const [filesViewMode, setFilesViewMode] = useState(fileViewModes.DETAILED_VIEW);
  const [tempFolder, setTempFolder] = useState<any>(projectMode ? {} : { id: "0" });
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [selectedFilesInIconsMode, setSelectedFilesInIconsMode] = useState<any[]>([]);
  const [isViewRendered, setIsViewRendered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Refs
  const splitterSizeRef = useRef([30, 70]);
  const tempRef = useRef<any>({});
  const treeViewRef = useRef(null);
  // Project mode loads the whole tree once (eager); navigating updates the URL hash → path, but that must
  // NOT re-trigger a read (it caused a double fetch + grid flash). This guards the [path] effect.
  const projectLoadedRef = useRef(false);

  // A Space-root tree node: top-level, identity is its real spaceId (not the '0' sentinel). Only exists
  // in project mode; replaces the old `id === "0"` root check everywhere it mattered.
  const isSpaceRoot = (node: any) => !!(projectMode && node && node.isSpaceRoot && node.spaceId);

  // Handlers
  const updateCurrentDir = (directory: any) => {
    setCurrentFolder(directory);
    setSelectedFolder(directory);
    setTempFolder(directory);
  };

  // Live handle to the loaded tree so navigateToFolder (exposed via the component ref for the app-header
  // breadcrumb) always resolves against the latest files, not a stale render's data.
  const fileManagerDataRef = useRef(fileManagerData);
  fileManagerDataRef.current = fileManagerData;

  // Navigate to a folder by id — the app-header breadcrumb calls this. Resolves the full tree node from the
  // loaded files and selects it, i.e. the same path a grid/tree click takes (the [selectedFolder] effect
  // then fetches its children and settles current/temp folder).
  const navigateToFolder = (folderId: string) => {
    const folder = (fileManagerDataRef.current?.files || []).find((f: any) => f.id === folderId);
    if (folder) setSelectedFolder(folder);
  };

  const generatePath = (folder: any) => {
    const innerPath = folder.parentIdArray?.filter((id: string) => id != "0").join("/");
    const prefixPath = innerPath ? `/${innerPath}` : "";
    const suffixPath = isSpaceRoot(folder) || folder.id == "0" ? "" : `${selectedFolder.id}/`;
    const finalPath = `${prefixPath}/${suffixPath}`;
    return finalPath;
  };

  const fetchData = async (allIds: any = null, setDirectory = true) => {
    const allParams: any = {
      action: "read",
      path: "/",
    };
    if (projectMode) {
      // Project ("Folders") mode: ONE read by projectId returns every Space as a tree root PLUS only the
      // focus Space's contents (lazy — other Spaces load on expand). `allIds` comes from a deep-link refresh
      // and is [spaceId, ...folderChain]: the first id focuses that Space, the rest load its ancestor chain
      // so a nested folder can be re-selected. Skips the single-space beforeSending stamping.
      const focusSpaceId = allIds?.[0];
      const chain = allIds?.slice(1) ?? [];
      allParams.data = [{ projectId, ...(focusSpaceId ? { spaceId: focusSpaceId } : {}) }];
      if (chain.length) {
        allParams.bulkParentIds = chain;
      }
    } else {
      if (allIds) {
        allParams.bulkParentIds = allIds;
      }
      beforeSending(allParams);
    }
    if (!allParams.cancel) {
      let allFiles: any[] = [];
      setRefreshGrid(true);
      // External-volume reads STREAM from a dedicated route; normal reads use /readFiles.
      const fsUrl = allParams.useAttachedExternalVolume
        ? `${getBackendUrl()}/fileSystemServer/readExternalVolume`
        : `${getBackendUrl()}/fileSystemServer/readFiles`;
      const res = await axiosWrapper.post<FileSystemReadResponse>(fsUrl, {
        ...allParams,
      });
      setIsLoaded(true);
      if (res.data?.files) {
        // cwd is the Space the server chose to open (default Space, else the first accessible one
        // alphabetically); in single-space mode it's the '0' root. updateCurrentDir selects it → the
        // [selectedFolder] effect seeds the grid. projectLoaded lets the page show the "not part of any
        // Space" state when the caller can access none (cwd empty).
        const directory = { ...res.data.cwd };
        setFileManagerData({
          ...res.data,
          customFieldsByFolder: projectMode ? {} : { "0": (res.data as any)?.customFields ?? [] },
          ...(projectMode ? { projectLoaded: true } : {}),
        });
        allFiles = res.data.files;
        if (setDirectory) {
          // Project mode: the focus Space's contents already arrived with this read, so mark the selection
          // isBulkFetch to skip the redundant on-demand re-fetch in the [selectedFolder] effect.
          // Single-space: force the cwd's id to the '0' root sentinel (getFileAttributes only sets
          // hierarchyId, leaving id undefined) so it matches the tree's synthetic root — the Space then
          // renders in the selected state with its root files shown.
          updateCurrentDir(projectMode ? { ...directory, isBulkFetch: true } : { ...directory, id: "0" });
        }
        onSuccess({
          result: res.data,
          action: "read",
          path: "/",
        });
      } else if (res.data.errorMessage) {
        if (projectMode) {
          setFileManagerData({ cwd: {}, files: [], projectLoaded: true });
        } else if (res.data.cwd) {
          const directory = { ...res.data.cwd, id: "0", isRowDisabled: true };
          setFileManagerData({
            cwd: directory,
            files: [],
          });
        }
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
      setRefreshGrid(false);
      return allFiles;
    }
  };

  const getMoreData = async () => {
    setRefreshGrid(true);
    const payload = { ...selectedFolder };
    const allParams: any = {
      action: "read",
      path: "/",
      data: [payload],
    };
    if (selectedFolder.id != "0") {
      const fetchPath = generatePath(selectedFolder);
      allParams.path = fetchPath;
    }
    beforeSending(allParams);
    if (!allParams.cancel) {
      const fsUrl = allParams.useAttachedExternalVolume
        ? `${getBackendUrl()}/fileSystemServer/readExternalVolume`
        : `${getBackendUrl()}/fileSystemServer/readFiles`;
      const res = await axiosWrapper.post<FileSystemReadResponse>(fsUrl, {
        ...allParams,
      });
      if (res.data?.files) {
        // Project mode: re-parent the fetched items into the multi-space tree. A Space-root read returns
        // top-level items with parentId "0", which must nest under the Space-root id; stamp spaceId on all
        // so the tree/grid filters and later writes resolve the right Space.
        const incomingFiles = (res.data?.files || []).map((file: any) =>
          projectMode
            ? {
                ...file,
                spaceId: selectedFolder.spaceId,
                parentId: file.parentId === "0" ? selectedFolder.spaceId : file.parentId,
              }
            : file,
        );
        const incomingFileIds = incomingFiles?.map((file: any) => file.id) || [];
        let freshCurrentFolder = { ...selectedFolder };
        // Single-space only: adopt the server cwd as the current node (a folder read returns its own
        // descriptor). In project mode the tree node is authoritative (already re-parented + spaceId), so
        // keep selectedFolder as the current folder.
        if (!projectMode && res.data?.cwd?._id && res.data?.cwd?._id != "0") {
          incomingFileIds.push(selectedFolder.id);
          freshCurrentFolder = res.data?.cwd;
          incomingFiles.push(freshCurrentFolder);
        }
        let currentFilteredFiles = fileManagerData.files?.filter((file: any) => !incomingFileIds.includes(file.id));
        currentFilteredFiles = currentFilteredFiles.filter((file: any) => file.parentId != selectedFolder.id);
        setFileManagerData((prev: any) => ({
          ...prev,
          files: [...incomingFiles, ...currentFilteredFiles],
          customFieldsByFolder: {
            ...(prev.customFieldsByFolder || {}),
            [freshCurrentFolder.id]: (res.data as any)?.customFields ?? [],
          },
        }));
        onSuccess({
          result: res.data,
          action: "read",
          path: allParams.path,
        });
        setCurrentFolder(freshCurrentFolder);
        setTempFolder(freshCurrentFolder);
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data?.errorMessage || ""),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
      setRefreshGrid(false);
    }
  };

  const fetchMoreData = async () => {
    if (projectMode) {
      // Lazy: fetch the selected node's children fresh on demand (Space root -> its first level; folder ->
      // its children). The initial focus selection is flagged isBulkFetch, so the [selectedFolder] effect
      // skips this for it (its data already arrived with the tree read). Locked Spaces/folders (no read) are
      // never fetched — just reflect the selection so the "No access" panel shows.
      if (!selectedFolder?.id) return;
      if (selectedFolder?.permission?.read === false) {
        setCurrentFolder(selectedFolder);
        setTempFolder(selectedFolder);
        return;
      }
      await getMoreData();
      return;
    }
    if (selectedFolder.id !== "0") {
      await getMoreData();
    } else {
      setTempFolder(selectedFolder);
      setCurrentFolder(selectedFolder);
    }
  };

  const refreshFileManager = () => {
    fetchData();
    if (!projectMode) setSelectedFolder({ id: "0" });
  };

  const refreshFiles = async () => {
    await getMoreData();
  };

  function extractIds() {
    return (path || "")
      .split("/")
      .filter(Boolean)
      .filter((id) => id != "0");
  }

  const fetchBulkData = async () => {
    const allIds: any = extractIds();
    const allFiles: any = await fetchData(allIds, false);
    const lastFolderId: any = allIds[allIds.length - 1];
    let lastFolder = allFiles?.find((file: any) => file.id == lastFolderId);
    // Project mode: if the deep-linked node is gone (deleted / no longer accessible / stale hash), fall back
    // to the focus Space root (allIds[0]) so the tree opens somewhere valid instead of selecting nothing.
    if (!lastFolder && projectMode) {
      lastFolder = allFiles?.find((file: any) => file.isSpaceRoot && file.id == allIds[0]);
    }
    if (lastFolder) {
      updateCurrentDir({ ...lastFolder, isBulkFetch: true });
    }
  };

  const handleSearchFiles = async (args: any) => {
    if (args) {
      setRefreshGrid(true);
      const payload = { ...selectedFolder };
      const allParams: any = {
        action: "search",
        caseSensitive: false,
        searchString: `*${args}*`,
        path: "/",
        data: [payload],
      };
      // Only narrow the search path to a specific folder when one is actually focused. A pre-search
      // can fire before the deep-link Space focus lands (selectedFolder still empty in project mode);
      // in that case keep path "/" for a Space-wide search rather than a malformed "/undefined/" path.
      if (selectedFolder.id && selectedFolder.id != "0") {
        const fetchPath = generatePath(selectedFolder);
        allParams.path = fetchPath;
      }
      beforeSending(allParams);
      if (!allParams.cancel) {
        delete allParams.cancel;
        // Search over an external volume streams from /readExternalVolume; otherwise /searchFiles.
        // (the search Space itself is stamped onto the payload data by beforeSending.)
        const fsUrl = allParams.useAttachedExternalVolume
          ? `${getBackendUrl()}/fileSystemServer/readExternalVolume`
          : `${getBackendUrl()}/fileSystemServer/searchFiles`;
        const res = await axiosWrapper.post<FileSystemReadResponse>(fsUrl, {
          ...allParams,
        });
        if (res.data) {
          setFileManagerData((prev: any) => ({
            ...prev,
            filteredFiles: [...(res.data.files ?? [])],
          }));
        }
        onSuccess({
          result: res.data,
          action: "search",
          path: allParams.path,
        });
        setRefreshGrid(false);
      }
    } else {
      setFileManagerData((prev: any) => ({
        ...prev,
        filteredFiles: null,
      }));
    }
  };

  const getSelectedFilesInFileManager = (filesRef: any) => {
    if (filesViewMode == fileViewModes.DETAILED_VIEW) {
      const selectedFile = filesRef.current.dataView.getAllSelectedItems();
      return selectedFile.filter(Boolean);
    } else if (filesViewMode == fileViewModes.LARGE_ICONS_VIEW) {
      return selectedFilesInIconsMode.filter(Boolean);
    }
    return [];
  };

  const showLoading = () => {
    setRefreshGrid(true);
  };

  const hideLoading = () => {
    setRefreshGrid(false);
  };

  // Delete files/folders
  const deleteFiles = async (filesToDelete: any[], deleteAssociatedTasks = true) => {
    if (!filesToDelete || filesToDelete.length === 0) return { success: false };

    try {
      const selectedFilesToDelete = filesToDelete.map((file: any) => ({
        ...file,
        // Prefer the file's own space (project mode spans many spaces); fall back to the single workspace.
        spaceId: file.spaceId ?? workspaceId,
        deleteAssociatedTasks,
      }));

      const data = {
        action: "delete",
        data: selectedFilesToDelete,
        path: "/",
      };

      beforeSending(data);

      const response = await axiosWrapper.post<FileSystemDeleteResponse>(
        `${getBackendUrl()}/fileSystemServer/deleteFiles`,
        data,
      );

      if (response.data.status) {
        // Remove deleted files from local state
        const deletedIds = filesToDelete.map((f: any) => f.id);
        setFileManagerData((prev: any) => ({
          ...prev,
          files: prev.files?.filter((f: any) => !deletedIds.includes(f.id)) || [],
        }));

        onDeleteSuccess(filesToDelete);
        dispatch(
          showToastNotification({
            content: filesToDelete.length === 1 ? t("Item deleted successfully") : t("Items deleted successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        return { success: true };
      } else {
        onDeleteError(response.data.message || response.data.errorMessage);
        dispatch(
          showToastNotification({
            content: untranslated(response.data.message || response.data.errorMessage || "Failed to delete"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return { success: false, error: response.data.message };
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : undefined;
      onDeleteError(errMsg || "Failed to delete");
      dispatch(
        showToastNotification({
          content: t("Failed to delete item"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return { success: false, error: errMsg };
    }
  };

  // Check if file can be deleted
  const canDeleteFile = (file: any) => {
    return file?.permission?.FOLDER_DELETE === true;
  };

  // Get current folder files (filtered and sorted)
  const getCurrentFolderFiles = (searchQuery = "") => {
    const data = fileManagerData.filteredFiles || fileManagerData.files || [];
    let items: any[] = [];

    if (tempFolder?.resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
      items = data.filter(
        (item: any) => item.transmittalVersionId === tempFolder.id && item.resourceType !== "RESOURCE_TYPE_TRANSMITTAL",
      );
    } else if (!tempFolder?.resourceType || tempFolder?.id === "0") {
      items = data.filter((item: any) => item.parentId === tempFolder?.id);
    } else {
      items = data.filter((item: any) => item.parentId === tempFolder?.id);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      items = items.filter((item: any) => item.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    // Sort: folders first, then files, alphabetically
    const folders = items.filter(
      (item: any) => item.resourceType === "RESOURCE_TYPE_FOLDER" || item.resourceType === "RESOURCE_TYPE_TRANSMITTAL",
    );
    const files = items.filter(
      (item: any) => item.resourceType !== "RESOURCE_TYPE_FOLDER" && item.resourceType !== "RESOURCE_TYPE_TRANSMITTAL",
    );

    const sortedFolders = [...folders].sort((a: any, b: any) => a.name?.localeCompare(b.name));
    const sortedFiles = [...files].sort((a: any, b: any) => a.name?.localeCompare(b.name));

    return [...sortedFolders, ...sortedFiles];
  };

  // Check if item is a folder
  const isFolder = (item: any) => {
    return item?.resourceType === "RESOURCE_TYPE_FOLDER" || item?.resourceType === "RESOURCE_TYPE_TRANSMITTAL";
  };

  // Check if file can be viewed
  const canViewFile = (file: any) => {
    return file?.permission?.read !== false;
  };

  // Effects
  useEffect(() => {
    let size: any = [];
    if (!showFolderTree) {
      size = [0, 100];
    } else {
      size = [30, 70];
    }
    splitterSizeRef.current = size;
  }, [showFolderTree]);

  useEffect(() => {
    if (disableAutoLoad) return;
    if (projectMode) {
      // Load the tree ONCE. Subsequent path changes (from folder navigation updating the #folderPath hash)
      // must not re-fetch — that fired the read twice and cleared the grid.
      if (projectLoadedRef.current) return;
      const allIds = extractIds();
      // On a browser refresh the folder path lands in `path` a tick after mount (null -> "/" -> #hash). If the
      // URL still carries a deep-link hash whose ids haven't arrived in `path` yet, wait — otherwise we'd load
      // the default Space now and the once-guard below would then block restoring the previously-open folder.
      const hasPendingDeepLink = typeof window !== "undefined" && window.location.hash.includes("folderPath");
      if (hasPendingDeepLink && !allIds.length) return;
      projectLoadedRef.current = true;
      // The hash carries [spaceId, ...folderChain]: focus that Space and restore the previously-open node
      // (bulkParentIds loads its ancestor chain). With no hash, open the default/first accessible Space.
      if (allIds.length) {
        fetchBulkData();
      } else {
        fetchData();
      }
      return;
    }
    if (path) {
      const allIds = extractIds();
      if (allIds.length) {
        fetchBulkData();
      } else {
        fetchData();
      }
    }
  }, [path]);

  useEffect(() => {
    if (disableAutoLoad) return;
    if (!selectedFolder.isBulkFetch) {
      fetchMoreData();
      setSelectedFilesInIconsMode([]);
      setFileManagerData((prev: any) => ({
        ...prev,
        filteredFiles: null,
      }));
    }
  }, [selectedFolder?.id]);

  // Project ("Folders") mode: publish the current folder path to redux so the FileExplorer page can mirror
  // it into the app-header breadcrumb. Crumbs are minimal {id,name}; a click resolves the full node by id
  // via navigateToFolder. Path shape mirrors the space-root breadcrumb in useFileManagerShared:
  // [Space root, ...ancestor folders, current folder].
  useEffect(() => {
    if (!projectMode) return;
    const files = fileManagerData?.files || [];
    const folder = selectedFolder;
    // Typed from the schema (BreadcrumbItem) rather than re-declared inline: this array is dispatched
    // straight into the fileManagerBreadCrumb slice, so the branded id type must come from the source.
    const crumbs: BreadcrumbItem[] = [];
    if (folder?.id) {
      const isCurrentSpaceRoot = !!(folder?.isSpaceRoot && folder?.spaceId);
      const spaceRoot = files.find((f: any) => f?.isSpaceRoot && f.spaceId === folder?.spaceId);
      if (spaceRoot) crumbs.push({ id: spaceRoot.id, name: spaceRoot.name });
      (folder?.parentIdArray || []).forEach((id: string) => {
        const obj = files.find((item: any) => item.id === id && !item.isSpaceRoot);
        if (obj) crumbs.push({ id: obj.id, name: obj.name });
      });
      if (!isCurrentSpaceRoot) crumbs.push({ id: folder.id, name: folder.name });
    }
    dispatch(fileManagerBreadCrumb(crumbs));
  }, [selectedFolder?.id, fileManagerData, projectMode]);

  // Clear the published path on unmount so it doesn't leak into other surfaces (projectMode only, so a
  // popup/single-space file manager unmounting can't wipe the Folders page's breadcrumb).
  useEffect(
    () => () => {
      if (projectMode) dispatch(fileManagerBreadCrumb([]));
    },
    [],
  );

  return {
    // State
    selectedFolder,
    setSelectedFolder,
    filesViewMode,
    setFilesViewMode,
    tempFolder,
    setTempFolder,
    refreshGrid,
    setRefreshGrid,
    selectedFilesInIconsMode,
    setSelectedFilesInIconsMode,
    isViewRendered,
    setIsViewRendered,
    isLoaded,
    setIsLoaded,

    // Refs
    splitterSizeRef,
    tempRef,
    treeViewRef,

    // Handlers
    updateCurrentDir,
    navigateToFolder,
    generatePath,
    fetchData,
    getMoreData,
    fetchMoreData,
    refreshFileManager,
    refreshFiles,
    extractIds,
    fetchBulkData,
    handleSearchFiles,
    getSelectedFilesInFileManager,
    showLoading,
    hideLoading,

    // File operations
    deleteFiles,
    canDeleteFile,
    getCurrentFolderFiles,
    isFolder,
    canViewFile,

    // Props passthrough
    workspaceId,
  };
}

export default useFileManagerComponent;
